import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@design-system/Sheet';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import { useGetSupplementListQuery } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import {
  findProductSectionsById,
  transformSupplementData,
} from '@screens/ShopPage/shopPageFunctions';
import { Loader } from '@design-system/Loader';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import EventTracker from '@utils/EventTracker';
import { analyticsEvent } from '@utils/analyticsEvent';
import type { SectionData } from '@screens/ShopPage/components/FormulaIngredientsSheet';

interface AddonIngredientsSheetProps {
  productId: number;
  ingredientCount: number;
  productName: string;
  productImageSrc: string;
}

export const AddonIngredientsSheet: React.FC<AddonIngredientsSheetProps> = ({
  productId,
  ingredientCount,
  productName,
  productImageSrc,
}) => {
  const {
    data: supplementListData,
    isLoading: isSupplementListLoading,
    error: isErrorInSupplementList,
  } = useGetSupplementListQuery([], []);

  const sectionDataFromApi = transformSupplementData(
    supplementListData ?? [],
  ) as SectionData;

  const matchingProductSections = findProductSectionsById(
    sectionDataFromApi,
    productId,
  );

  if (isSupplementListLoading) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (isErrorInSupplementList) {
    return <ErrorLoadingPage errorType="error" hideNavBar />;
  }

  const handleTrackEvent = () => {
    EventTracker.trackEvent(
      'shop',
      analyticsEvent.SHOP_ADDONDETAILS_MYINGREDIENTS,
    );
  };

  return (
    <Sheet>
      <SheetTrigger onClick={handleTrackEvent}>
        <button className="flex items-center rounded-md pl-4 pr-2 py-3 typography-btn-medium text-secondary justify-center transition-colors bg-gradient-to-b from-[#FCF2EB] to-[#FCF9E3]">
          {ingredientCount} Ingredients{' '}
          <ChevronRight className="text-secondary-foreground h-4 w-4 mt-0.5 ml-1" />
        </button>
      </SheetTrigger>
      <SheetContent className="w-full md:w-[400px]">
        <SheetHeader className="pt-4 pb-2 px-6">
          <br />
          <div className="flex items-center gap-2 !mb-2">
            <img
              src={productImageSrc}
              alt=""
              aria-hidden
              className="h-12 w-12 object-contain"
            />
            <span className="typography-h3 text-primary">
              {productName}
            </span>
          </div>
          <div>
            <p className="typography-h1 text-primary mb-4">
              {ingredientCount} Ingredients
            </p>
          </div>
        </SheetHeader>
        <div className="p-6 overflow-y-auto">
          {matchingProductSections.length > 0 ? (
            matchingProductSections.map((section, sectionIndex) => (
              <div
                key={`section-${section.productId}-${sectionIndex}-${section.sectionTitle || 'default'}`}
                className="mb-6 last:mb-0"
              >
                {section.sectionTitle && section.items.length > 0 && (
                  <div className="mb-4">
                    <p className="typography-h3 text-primary">
                      {section.sectionTitle}
                    </p>
                    <hr className="mt-6 border-secondary border-[1.5px]" />
                  </div>
                )}
                <div>
                  {(section.items || []).map((ingredient, ingredientIndex) => (
                    <div
                      key={`${ingredient.id}-${ingredientIndex}`}
                      className="flex justify-between items-center py-3 border-b border-secondary last:border-b-0"
                    >
                      <p className="typography-title text-secondary flex-1 mr-4">
                        {ingredient.name}
                      </p>
                      <p className="typography-body text-secondary">
                        {ingredient.volume ?? ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center typography-body text-gray-500 mt-4">
              No specific ingredients listed for this product.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
